import React, { useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, FreeMode } from 'swiper/modules';
import ListingCard from 'components/listing-card/listing-card-v2';

import { listingCardIds } from 'constants/test-constants';
import styles from './style.module.scss';
import useSizeClass from 'hooks/use-size-class';
import CarouselArrows from 'components/swiper-carousel/components/arrows';
import LoadingSkeleton from 'components/loading-skeleton';

import type SwiperClass from 'swiper/types/swiper-class';
import type { SimilarListing, ComparableListing } from '../similar-listings';
import type { DecorationsProps, ListingCardProps } from 'components/listing-card/listing-card-v2';

//#region Types

type SimilarListingsCarouselProps = {
  listings: SimilarListing[];
  listingToCompare?: ComparableListing;
  /**
   * The function to call when a listing card is clicked.
   */
  onListingCardClick: ListingCardProps['onListingCardClick'];
};
//#endregion

function SimilarListingsCarouselSkeleton() {
  return (
    <div className={styles.component} data-testid={listingCardIds.listingsSwiper}>
      <div className={styles.slider}>
        <LoadingSkeleton className={styles.card} />
      </div>
    </div>
  );
}

//#region Component
export default function SimilarListingsCarousel({ listings, listingToCompare, onListingCardClick }: SimilarListingsCarouselProps) {
  const [swiper, setSwiper] = useState<SwiperClass>();
  const isDesktop = useSizeClass({ min: 1000 });
  const listingsCount = (listings || []).length;
  const shouldLoopBeEnabled = (listings || []).length >= 3;
  const isNotFull = (listings || []).length <= 3 && isDesktop;

  const decorations = useMemo<DecorationsProps['decorations']>(() => (
    { comparableData: {
      bedrooms: listingToCompare?.bedrooms,
      bathrooms: listingToCompare?.bathrooms,
      avgSqft: listingToCompare?.avgSqft,
      sqftLabel: listingToCompare?.sqftLabel,

    }}), [
    listingToCompare?.bedrooms,
    listingToCompare?.bathrooms,
    listingToCompare?.avgSqft,
    listingToCompare?.sqftLabel,
  ]);

  const slideTo = (slide: 'next' | 'prev' | number) => {
    if (slide === 'next') {
      swiper?.slideNext();
    } else if (slide === 'prev') {
      swiper?.slidePrev();
    }
  };

  if (!listings || listingsCount === 0) {
    return (<SimilarListingsCarouselSkeleton />);
  }

  return (
    <div className={styles.component} data-testid={listingCardIds.listingsSwiper}>
      <Swiper
        onSwiper={setSwiper}
        className={styles.slider}
        loop={shouldLoopBeEnabled}
        slidesPerView={'auto'}
        spaceBetween={20}
        freeMode={isNotFull ? false : true}
        mousewheel={{
          releaseOnEdges: true,
          forceToAxis: true,
        }}
        modules={[Mousewheel, FreeMode]}
      >
        { listingsCount > 0 &&
          listings.map((listing, index) => (
            <SwiperSlide virtualIndex={index} key={listing.id} className={styles.card}>
              <ListingCard listing={listing} decorations={decorations} onListingCardClick={onListingCardClick} compliance={listing?.compliance} />
            </SwiperSlide>
          ))
        }
      </Swiper>
      {!isNotFull &&
        <CarouselArrows isLightbox={false} isListingCardCarousel={true} slideTo={slideTo} className={styles.arrow} />
      }
    </div>
  );
}
//#endregion